import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type {
  CreateTaskRequest,
  Task,
  TaskFilter,
  TaskPriority,
  TaskSortOptions,
  TaskStatus,
  TaskType,
} from '@/types/task.types'

export interface TaskManagementState {
  // Current selection
  selectedTasks: string[]
  selectedTask: Task | null

  // Filters and sorting
  filters: TaskFilter
  sort: TaskSortOptions

  // UI state
  view: 'table' | 'kanban' | 'calendar'
  showFilters: boolean
  showTaskDetails: boolean
  showCreateTaskDialog: boolean
  showBulkOperations: boolean

  // Quick filters (commonly used filters)
  quickFilters: {
    myTasks: boolean
    overdue: boolean
    highPriority: boolean
    unassigned: boolean
  }

  // Search and pagination
  searchQuery: string
  currentPage: number
  pageSize: number

  // Task creation state
  taskCreation: {
    isOpen: boolean
    templateId?: string
    enrollmentId?: string
    citizenId?: string
    prefillData?: Partial<CreateTaskRequest>
  }

  // Cache and sync
  lastRefresh: string | null
  autoRefresh: boolean
  refreshInterval: number

  // Preferences
  preferences: {
    defaultView: 'table' | 'kanban' | 'calendar'
    defaultSort: TaskSortOptions
    columnsVisible: string[]
    autoAssignToSelf: boolean
    showCompletedTasks: boolean
    notificationsEnabled: boolean
  }

  // Statistics display preferences
  statisticsView: {
    period: 'today' | 'week' | 'month' | 'custom'
    customDateRange?: {
      start: string
      end: string
    }
    showByType: boolean
    showByPriority: boolean
    showPerformance: boolean
  }
}

const initialState: TaskManagementState = {
  // Selection
  selectedTasks: [],
  selectedTask: null,

  // Filters and sorting
  filters: {},
  sort: {
    field: 'createdAt',
    direction: 'desc',
  },

  // UI state
  view: 'table',
  showFilters: false,
  showTaskDetails: false,
  showCreateTaskDialog: false,
  showBulkOperations: false,

  // Quick filters
  quickFilters: {
    myTasks: false,
    overdue: false,
    highPriority: false,
    unassigned: false,
  },

  // Search and pagination
  searchQuery: '',
  currentPage: 1,
  pageSize: 20,

  // Task creation
  taskCreation: {
    isOpen: false,
  },

  // Cache and sync
  lastRefresh: null,
  autoRefresh: true,
  refreshInterval: 30000, // 30 seconds

  // Preferences
  preferences: {
    defaultView: 'table',
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    columnsVisible: ['title', 'type', 'priority', 'status', 'assignee', 'dueDate'],
    autoAssignToSelf: false,
    showCompletedTasks: false,
    notificationsEnabled: true,
  },

  // Statistics
  statisticsView: {
    period: 'today',
    showByType: true,
    showByPriority: true,
    showPerformance: false,
  },
}

const taskManagementSlice = createSlice({
  name: 'taskManagement',
  initialState,
  reducers: {
    // Selection actions
    setSelectedTasks: (state, action: PayloadAction<string[]>) => {
      state.selectedTasks = action.payload
    },

    toggleTaskSelection: (state, action: PayloadAction<string>) => {
      const taskId = action.payload
      const index = state.selectedTasks.indexOf(taskId)
      if (index >= 0) {
        state.selectedTasks.splice(index, 1)
      } else {
        state.selectedTasks.push(taskId)
      }
    },

    clearSelection: (state) => {
      state.selectedTasks = []
    },

    setSelectedTask: (state, action: PayloadAction<Task | null>) => {
      state.selectedTask = action.payload
    },

    // Filter actions
    setFilters: (state, action: PayloadAction<Partial<TaskFilter>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.currentPage = 1 // Reset to first page when filters change
    },

    clearFilters: (state) => {
      state.filters = {}
      state.currentPage = 1
    },

    setStatusFilter: (state, action: PayloadAction<TaskStatus[] | undefined>) => {
      if (action.payload === undefined) {
        delete state.filters.status
      } else {
        state.filters.status = action.payload
      }
      state.currentPage = 1
    },

    setPriorityFilter: (state, action: PayloadAction<TaskPriority[] | undefined>) => {
      if (action.payload === undefined) {
        delete state.filters.priority
      } else {
        state.filters.priority = action.payload
      }
      state.currentPage = 1
    },

    setTypeFilter: (state, action: PayloadAction<TaskType[] | undefined>) => {
      if (action.payload === undefined) {
        delete state.filters.type
      } else {
        state.filters.type = action.payload
      }
      state.currentPage = 1
    },

    setAssigneeFilter: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload === undefined) {
        delete state.filters.assigneeId
      } else {
        state.filters.assigneeId = action.payload
      }
      state.currentPage = 1
    },

    // Quick filter actions
    setQuickFilters: (state, action: PayloadAction<Partial<typeof initialState.quickFilters>>) => {
      state.quickFilters = { ...state.quickFilters, ...action.payload }

      // Apply quick filter logic to main filters
      if (action.payload.myTasks !== undefined) {
        if (action.payload.myTasks) {
          // Set assignee to current user (would be set from session)
          state.filters.assigneeId = 'current-user'
        } else if (!Object.values(state.quickFilters).some((v) => v)) {
          // Clear assignee filter if no quick filters are active
          delete state.filters.assigneeId
        }
      }

      if (action.payload.overdue !== undefined) {
        state.filters.hasOverdue = action.payload.overdue
      }

      if (action.payload.highPriority !== undefined) {
        if (action.payload.highPriority) {
          state.filters.priority = ['high', 'urgent']
        } else if (!state.quickFilters.highPriority) {
          delete state.filters.priority
        }
      }

      if (action.payload.unassigned !== undefined) {
        state.filters.isUnassigned = action.payload.unassigned
      }

      state.currentPage = 1
    },

    clearQuickFilters: (state) => {
      state.quickFilters = {
        myTasks: false,
        overdue: false,
        highPriority: false,
        unassigned: false,
      }
    },

    // Sort actions
    setSort: (state, action: PayloadAction<TaskSortOptions>) => {
      state.sort = action.payload
    },

    toggleSort: (state, action: PayloadAction<TaskSortOptions['field']>) => {
      const field = action.payload
      if (state.sort.field === field) {
        state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        state.sort.field = field
        state.sort.direction = 'desc'
      }
    },

    // Search actions
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
      if (action.payload) {
        state.filters.search = action.payload
      } else {
        delete state.filters.search
      }
      state.currentPage = 1
    },

    clearSearch: (state) => {
      state.searchQuery = ''
      delete state.filters.search
      state.currentPage = 1
    },

    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
      state.currentPage = 1
    },

    // View actions
    setView: (state, action: PayloadAction<TaskManagementState['view']>) => {
      state.view = action.payload
    },

    toggleFilters: (state) => {
      state.showFilters = !state.showFilters
    },

    setShowFilters: (state, action: PayloadAction<boolean>) => {
      state.showFilters = action.payload
    },

    setShowTaskDetails: (state, action: PayloadAction<boolean>) => {
      state.showTaskDetails = action.payload
    },

    setShowCreateTaskDialog: (state, action: PayloadAction<boolean>) => {
      state.showCreateTaskDialog = action.payload
    },

    setShowBulkOperations: (state, action: PayloadAction<boolean>) => {
      state.showBulkOperations = action.payload
    },

    // Task creation actions
    openTaskCreation: (
      state,
      action: PayloadAction<{
        templateId?: string
        enrollmentId?: string
        citizenId?: string
        prefillData?: Partial<CreateTaskRequest>
      }>
    ) => {
      state.taskCreation = {
        isOpen: true,
        ...action.payload,
      }
      state.showCreateTaskDialog = true
    },

    closeTaskCreation: (state) => {
      state.taskCreation = {
        isOpen: false,
      }
      state.showCreateTaskDialog = false
    },

    // Cache and sync actions
    setLastRefresh: (state, action: PayloadAction<string>) => {
      state.lastRefresh = action.payload
    },

    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    // Preferences actions
    setPreferences: (state, action: PayloadAction<Partial<TaskManagementState['preferences']>>) => {
      state.preferences = { ...state.preferences, ...action.payload }
    },

    setDefaultView: (state, action: PayloadAction<TaskManagementState['view']>) => {
      state.preferences.defaultView = action.payload
      state.view = action.payload
    },

    setColumnsVisible: (state, action: PayloadAction<string[]>) => {
      state.preferences.columnsVisible = action.payload
    },

    toggleColumnVisibility: (state, action: PayloadAction<string>) => {
      const column = action.payload
      const index = state.preferences.columnsVisible.indexOf(column)
      if (index >= 0) {
        state.preferences.columnsVisible.splice(index, 1)
      } else {
        state.preferences.columnsVisible.push(column)
      }
    },

    setAutoAssignToSelf: (state, action: PayloadAction<boolean>) => {
      state.preferences.autoAssignToSelf = action.payload
    },

    setShowCompletedTasks: (state, action: PayloadAction<boolean>) => {
      state.preferences.showCompletedTasks = action.payload

      // Update status filter based on preference
      if (action.payload) {
        // Include completed tasks
        if (state.filters.status) {
          if (!state.filters.status.includes('completed')) {
            state.filters.status.push('completed')
          }
        }
      } else {
        // Exclude completed tasks
        if (state.filters.status) {
          state.filters.status = state.filters.status.filter((status) => status !== 'completed')
          if (state.filters.status.length === 0) {
            delete state.filters.status
          }
        }
      }
    },

    setNotificationsEnabled: (state, action: PayloadAction<boolean>) => {
      state.preferences.notificationsEnabled = action.payload
    },

    // Statistics view actions
    setStatisticsView: (
      state,
      action: PayloadAction<Partial<TaskManagementState['statisticsView']>>
    ) => {
      state.statisticsView = { ...state.statisticsView, ...action.payload }
    },

    setStatisticsPeriod: (
      state,
      action: PayloadAction<TaskManagementState['statisticsView']['period']>
    ) => {
      state.statisticsView.period = action.payload
      if (action.payload !== 'custom') {
        delete state.statisticsView.customDateRange
      }
    },

    setCustomDateRange: (state, action: PayloadAction<{ start: string; end: string }>) => {
      state.statisticsView.customDateRange = action.payload
      state.statisticsView.period = 'custom'
    },

    // Bulk actions
    selectAllTasks: (state, action: PayloadAction<string[]>) => {
      state.selectedTasks = action.payload
    },

    selectAllVisibleTasks: (state, action: PayloadAction<string[]>) => {
      state.selectedTasks = [...new Set([...state.selectedTasks, ...action.payload])]
    },

    // Reset actions
    resetToDefaults: (state) => {
      return {
        ...initialState,
        preferences: state.preferences, // Keep user preferences
      }
    },

    resetFilters: (state) => {
      state.filters = {}
      state.quickFilters = initialState.quickFilters
      state.searchQuery = initialState.searchQuery
      state.currentPage = 1
    },

    resetSelection: (state) => {
      state.selectedTasks = []
      state.selectedTask = null
    },

    // Advanced filter presets
    applyFilterPreset: (
      state,
      action: PayloadAction<{
        name: string
        filters: TaskFilter
        sort?: TaskSortOptions
      }>
    ) => {
      const { filters, sort } = action.payload
      state.filters = filters
      if (sort) {
        state.sort = sort
      }
      state.currentPage = 1
    },

    // Task interaction tracking
    trackTaskView: (_state, _action: PayloadAction<string>) => {
      // Could be used for analytics or recently viewed tasks
      // Implementation would depend on requirements
    },

    // Notification handling
    markNotificationRead: (_state, _action: PayloadAction<string>) => {
      // Mark task-related notifications as read
      // Implementation would depend on notification system
    },
  },
})

export const {
  // Selection actions
  setSelectedTasks,
  toggleTaskSelection,
  clearSelection,
  setSelectedTask,

  // Filter actions
  setFilters,
  clearFilters,
  setStatusFilter,
  setPriorityFilter,
  setTypeFilter,
  setAssigneeFilter,

  // Quick filter actions
  setQuickFilters,
  clearQuickFilters,

  // Sort actions
  setSort,
  toggleSort,

  // Search actions
  setSearchQuery,
  clearSearch,

  // Pagination actions
  setCurrentPage,
  setPageSize,

  // View actions
  setView,
  toggleFilters,
  setShowFilters,
  setShowTaskDetails,
  setShowCreateTaskDialog,
  setShowBulkOperations,

  // Task creation actions
  openTaskCreation,
  closeTaskCreation,

  // Cache and sync actions
  setLastRefresh,
  setAutoRefresh,
  setRefreshInterval,

  // Preferences actions
  setPreferences,
  setDefaultView,
  setColumnsVisible,
  toggleColumnVisibility,
  setAutoAssignToSelf,
  setShowCompletedTasks,
  setNotificationsEnabled,

  // Statistics actions
  setStatisticsView,
  setStatisticsPeriod,
  setCustomDateRange,

  // Bulk actions
  selectAllTasks,
  selectAllVisibleTasks,

  // Reset actions
  resetToDefaults,
  resetFilters,
  resetSelection,

  // Advanced actions
  applyFilterPreset,
  trackTaskView,
  markNotificationRead,
} = taskManagementSlice.actions

export default taskManagementSlice.reducer

// Selectors
export const selectTaskManagement = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement
export const selectSelectedTasks = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.selectedTasks
export const selectSelectedTask = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.selectedTask
export const selectFilters = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.filters
export const selectSort = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.sort
export const selectQuickFilters = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.quickFilters
export const selectView = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.view
export const selectPreferences = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.preferences
export const selectStatisticsView = (state: { taskManagement: TaskManagementState }) =>
  state.taskManagement.statisticsView

// Computed selectors
export const selectActiveFiltersCount = (state: { taskManagement: TaskManagementState }) => {
  const { filters } = state.taskManagement
  let count = 0

  if (filters.status?.length) count++
  if (filters.priority?.length) count++
  if (filters.type?.length) count++
  if (filters.assigneeId) count++
  if (filters.search) count++
  if (filters.dueDateFrom || filters.dueDateTo) count++
  if (filters.hasOverdue) count++
  if (filters.isUnassigned) count++
  if (filters.tags?.length) count++

  return count
}

export const selectHasActiveQuickFilters = (state: { taskManagement: TaskManagementState }) => {
  const { quickFilters } = state.taskManagement
  return Object.values(quickFilters).some((filter) => filter)
}
